import axios from 'axios';
import { Session } from '@supabase/supabase-js';
// Configuration - Update this to match your backend URL
import API_BASE_URL from '../config/api';

export interface DataDownloadRequest {
  hasRequest: boolean;
  status: 'pending' | 'processing' | 'ready' | 'expired' | 'failed' | null;
  apiStatus?: 'ready' | 'expired' | 'processing' | 'failed'; // Added to show the outer API status
  downloadUrl?: string;
  fileName?: string;
  fileSize?: number;
  expiresAt?: string;
  createdAt?: string;
  updatedAt?: string;
  message?: string;
}

export interface ApiResponse<T> {
  code: number;
  status: 'success' | 'failed';
  data?: T;
  error?: string;
  details?: string;
}

class DataDownloadService {
  /**
   * Check the status of user's data download request
   */
  async checkDownloadStatus(userId: string, session: Session): Promise<DataDownloadRequest> {
    try {      
      const response = await axios.get<ApiResponse<DataDownloadRequest>>(
        `${API_BASE_URL}/api/user/data-download-status`,
        {
          params: { userId },
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
          }
        }
      );
      
      if (response.data.status === 'success' && response.data.data) {
        return {
          ...response.data.data,
          apiStatus: ['processing', 'ready', 'expired', 'failed'].includes(response.data.data.status as string)
            ? response.data.data.status as 'processing' | 'ready' | 'expired' | 'failed'
            : undefined
        };
      }

      throw new Error(response.data.error || 'Failed to check download status');
    } catch (error) {
      
      if (axios.isAxiosError(error)) {
        
        if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
          throw new Error('Cannot connect to server.');
        }
        
        if (error.response?.status === 500) {
          throw new Error('Server error.');
        }
        
        if (error.response?.status === 400) {
          throw new Error(error.response.data?.error || 'Invalid request.');
        }
        
        const errorMessage = error.response?.data?.error || error.message;
        throw new Error(`Failed to check download status: ${errorMessage}`);
      }
      throw error;
    }
  }

  /**
   * Create a new data download request
   */
  async createDownloadRequest(userId: string, session: Session, userEmail?: string): Promise<{
    message: string;
    requestId: string;
    status: string;
    createdAt: string;
  }> {
    try {
      const response = await axios.post<ApiResponse<{
        message: string;
        requestId: string;
        status: string;
        createdAt: string;
      }>>(
        `${API_BASE_URL}/api/user/data-download-request`,
        {
          userId,
          userEmail
        },
        {
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
          }
        }
      );

      if (response.data.status === 'success' && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to create download request');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        
        if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
          throw new Error('Cannot connect to server.');
        }
        
        if (error.response?.status === 500) {
          throw new Error('Server error. The data_download_requests table might not exist in the database. Please check the backend setup.');
        }
        
        if (error.response?.status === 400) {
          throw new Error(error.response.data?.error || 'Invalid request. Please check your user information.');
        }
        
        const errorMessage = error.response?.data?.error || error.message;
        throw new Error(`Failed to create download request: ${errorMessage}`);
      }
      throw error;
    }
  }

  /**
   * Get download URL when data is ready
   */
  async getDownloadUrl(userId: string, session: Session): Promise<{
    downloadUrl: string;
    fileName: string;
    fileSize: number;
    expiresAt: string;
    createdAt: string;
  }> {
    try {
      const response = await axios.get<ApiResponse<{
        downloadUrl: string;
        fileName: string;
        fileSize: number;
        expiresAt: string;
        createdAt: string;
      }>>(
        `${API_BASE_URL}/api/user/data-download-url`,
        {
          params: { userId },
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
          }
        }
      );

      if (response.data.status === 'success' && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to get download URL');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.error || error.message;
        throw new Error(`Failed to get download URL: ${errorMessage}`);
      }
      throw error;
    }
  }

  /**
   * Download the file directly (triggers browser download)
   */
  async downloadFile(downloadUrl: string, fileName: string, session: Session): Promise<void> {
    try {
      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        }
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.error || error.message;
        throw new Error(`Failed to download file: ${errorMessage}`);
      }
      throw error;
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Calculate time remaining until expiration
   */
  getTimeUntilExpiration(expiresAt: string): string {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Expired';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 24) {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  }
}

export const dataDownloadService = new DataDownloadService();
